import { BaseApiClient } from './base-client';
import { BillingInfo, BillingOverview, Invoice, InvoiceData, PaginatedInvoices, InvoiceFilter } from '../types/billing';

export class BillingApiClient extends BaseApiClient {
  async getBillingOverview(): Promise<BillingOverview> {
    return this.get<BillingOverview>('/api/v1/billing/');
  }

  async getInvoices(page: number = 1, perPage: number = 10, filters?: InvoiceFilter): Promise<PaginatedInvoices> {
    const params: any = { page, per_page: perPage };
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
    if (filters?.payment_type) params.payment_type = filters.payment_type;
    if (filters?.service_id) params.service_id = filters.service_id;
    
    return this.get<PaginatedInvoices>('/api/v1/billing/invoices', { params });
  }

  async getInvoice(invoiceId: number): Promise<Invoice> {
    return this.get<Invoice>(`/api/v1/billing/invoices/${invoiceId}`);
  }

  async getInvoiceData(invoiceId: number): Promise<InvoiceData> {
    return this.get<InvoiceData>(`/api/v1/billing/invoices/${invoiceId}`);
  }

  async downloadInvoicePdf(invoiceId: number): Promise<Blob> {
    const response = await this.axios.get(
      `/api/v1/billing/invoices/${invoiceId}/pdf`,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
    return response.data;
  }

  // Helper to trigger download in browser
  async downloadInvoice(invoiceId: number, invoiceNumber: string) {
    const blob = await this.downloadInvoicePdf(invoiceId);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoice_${invoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Get invoice preview HTML with authentication
  async getInvoicePreview(invoiceId: number): Promise<string> {
    const response = await this.get<string>(`/api/v1/billing/invoices/${invoiceId}/preview`, {
      headers: {
        'Accept': 'text/html'
      }
    });
    return response;
  }

  async updateBillingInfo(info: Partial<BillingInfo>): Promise<BillingInfo> {
    return this.put<BillingInfo>('/api/v1/billing/info', info);
  }

  // Helper method to format currency amount
  formatCurrency(amount: number, currency: string = 'EUR'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount / 100); // Convert cents to whole currency units
  }

  // Helper method to format date
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Helper method to get payment type display text
  getPaymentTypeDisplay(paymentType: string): string {
    const typeMap: { [key: string]: string } = {
      PURCHASE: 'New Service',
      RENEWAL: 'Service Renewal',
      PLAN_CHANGE: 'Plan Change'
    };
    return typeMap[paymentType] || paymentType;
  }

  // Helper method to get payment type color
  getPaymentTypeColor(paymentType: string): string {
    const colorMap: { [key: string]: string } = {
      PURCHASE: 'blue',
      RENEWAL: 'green',
      PLAN_CHANGE: 'orange'
    };
    return colorMap[paymentType] || 'gray';
  }

  // Helper method to format billing period
  formatBillingPeriod(startDate: string, endDate: string): string {
    const start = this.formatDate(startDate);
    const end = this.formatDate(endDate);
    return `${start} - ${end}`;
  }

  // Helper method to check if billing info is complete
  isBillingInfoComplete(info: BillingInfo): boolean {
    if (!info) return false;

    // For business accounts
    if (info.company_name) {
      return !!(
        info.company_name &&
        info.vat_number &&
        info.billing_address?.street &&
        info.billing_address?.city &&
        info.billing_address?.country &&
        info.billing_address?.postal_code
      );
    }

    // For personal accounts
    return !!(
      info.billing_address?.street &&
      info.billing_address?.city &&
      info.billing_address?.country &&
      info.billing_address?.postal_code
    );
  }

  // Helper method to get billing address display string
  formatBillingAddress(info: BillingInfo): string {
    if (!info.billing_address) return '';

    const parts = [
      info.billing_address.street,
      info.billing_address.city,
      info.billing_address.state,
      info.billing_address.postal_code,
      info.billing_address.country
    ].filter(Boolean);

    return parts.join(', ');
  }

  // Helper method to format invoice amount (converts from cents)
  formatInvoiceAmount(amountInCents: number, currency: string = 'EUR'): string {
    return this.formatCurrency(amountInCents, currency);
  }
}
